import { Link } from 'react-router-dom';
import clsx from 'clsx';

const LOGO_PNG = '/shopgnd-logo.png';
const LOGO_WIDTH = 213;
const LOGO_HEIGHT = 157;
const LOGO_ASPECT = `${LOGO_WIDTH} / ${LOGO_HEIGHT}`;

/** Responsive WebP variants — cap at 88w for navbar (display ≤88 CSS px). */
const LOGO_WEBP_SRCSET = [
  '/shopgnd-logo-64.webp 64w',
  '/shopgnd-logo-88.webp 88w',
].join(', ');

/** Max rendered width in navbar/footer (~h-12–h-16). */
const LOGO_SIZES = '(max-width: 640px) 72px, 88px';

/** Default navbar-sized logo (compact on phones). */
export const LOGO_SIZE_NAV =
  'h-8 sm:h-9 md:h-11 lg:h-12';

/** Footer / marketing blocks — slightly larger but still reasonable on mobile. */
export const LOGO_SIZE_FOOTER = 'h-12 sm:h-14 md:h-16';

type ShopGndLogoProps = {
  /** Link target; omit or pass `null` for a non-clickable logo. */
  to?: string | null;
  className?: string;
  /** All variants use the transparent PNG master; `onDark` is kept for call-site compatibility only. */
  variant?: 'default' | 'onLight' | 'onDark';
  /**
   * `sizes` hint for responsive WebP (defaults to navbar/footer max ~88px wide).
   * Use a smaller value (e.g. `52px`) for compact logos (`h-7`, `h-8`).
   */
  sizes?: string;
  /** Homepage LCP — preload hint for the navbar logo only. */
  priority?: boolean;
};

export function ShopGndLogo({
  to = '/',
  className = LOGO_SIZE_NAV,
  variant: _variant = 'default',
  sizes = LOGO_SIZES,
  priority = false,
}: ShopGndLogoProps) {

  const img = (
    <picture className="block w-fit max-w-full leading-none">
      <source srcSet={LOGO_WEBP_SRCSET} sizes={sizes} type="image/webp" />
      <img
        src={LOGO_PNG}
        alt="ShopGND"
        width={LOGO_WIDTH}
        height={LOGO_HEIGHT}
        decoding="async"
        {...(priority ? { fetchPriority: 'high' as const } : {})}
        className={clsx(
          'block w-auto max-w-full object-contain object-left',
          className,
        )}
        style={{ aspectRatio: LOGO_ASPECT }}
      />
    </picture>
  );

  const shellClass = clsx(
    'inline-flex w-fit max-w-full shrink-0 items-center leading-none',
    to !== null && 'transition-opacity hover:opacity-90',
  );

  if (to === null) {
    return <span className={shellClass}>{img}</span>;
  }

  return (
    <Link to={to ?? '/'} className={shellClass}>
      {img}
    </Link>
  );
}
